import NiceModal, { antdModal, useModal } from "@ebay/nice-modal-react";
import { Button, Form, Modal } from "antd";
import { ButtonType } from "antd/es/button";
import { CSSProperties, useCallback, useMemo } from "react";

export type BaseModalProps = {
  initialValue: any;
  title: string;
  onSubmit: (values: any) => void;
  onCancel: () => void;
  children: any;
  footer?: Array<BaseModalButtonProps>;
  style?: CSSProperties;
  className?: string;
};

export type BaseModalButtonProps = {
  title: string;
  htmlType: "submit" | "button";
  type?: ButtonType;
  onClick?: (values: any) => void;
  className?: string;
};

export default NiceModal.create((props: BaseModalProps) => {
  const modal = useModal();
  const [form] = Form.useForm();

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then(() => {
        modal.resolve(form.getFieldsValue());
        modal.hide();
      })
      .catch((error) => {
        console.log("Validate error: ", error);
      });
  }, [modal, form]);

  const handleCancel = useCallback(() => {
    modal.reject(new Error("USER_CANCEL"));
    modal.hide();
  }, [modal, form]);

  const generateFooter = useMemo(() => {
    if (!props.footer || props.footer.length === 0) return null;

    const isSingleButton = props.footer.length === 1;
    const isSingleSubmitButton =
      isSingleButton && props.footer[0].htmlType === "submit";
    const isMultipleButtons = props.footer.length > 1;

    const footerButtons = props.footer.map((e, index) => {
      const defaultClass = isSingleSubmitButton
        ? "w-full"
        : isMultipleButtons
          ? "w-[140px] md:w-[180px] mx-2"
          : "";

      const buttonClass = `${defaultClass} ${e.className ?? ""}`.trim();

      return (
        <Button
          key={index}
          type={e.type}
          htmlType={e.htmlType}
          className={buttonClass}
          onClick={() => {
            if (e.onClick) {
              e.onClick(form.getFieldsValue());
            } else if (e.htmlType === "submit") {
              handleSubmit();
            } else {
              handleCancel();
            }
          }}
        >
          {e.title}
        </Button>
      );
    });

    return (
      <div
        className={
          isSingleSubmitButton || isMultipleButtons ? "text-center" : ""
        }
      >
        {footerButtons}
      </div>
    );
  }, [props, form, handleSubmit, handleCancel]);

  return (
    <Modal
      {...antdModal(modal)}
      title={props.title}
      okText={props.title}
      className={props.className}
      onOk={handleSubmit}
      onCancel={handleCancel}
      footer={generateFooter}
    >
      <Form form={form}>{props.children}</Form>
    </Modal>
  );
});
